import { zodResolver } from '@hookform/resolvers/zod';
import { assert } from 'assert-ts';
import { dequal as deepEqual } from 'dequal/lite';
import { useEffect, useRef } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { handleServerFormError, trackError, ValidationError } from '#api/errors.ts';
import { useSuspenseFeatureFlagsQuery } from '#api/org.queries.ts';
import { InfoCircle } from '#assets/ReactSvg.ts';
import { Banner } from '#components/atoms/banner/Banner.tsx';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { ErrorMessage } from '#components/atoms/forms/index.ts';
import { ConfirmChangesBanner } from '#components/patterns/confirmations/confirm-changes-banner/ConfirmChangesBanner.tsx';
import { CancelLinkButton } from '#components/patterns/forms/cancel-link-button/CancelLinkButton.tsx';
import { TabLink, TabLinks } from '#components/patterns/tab-links/index.ts';
import type { ConfigOverride } from '#dn-types/host.ts';
import { usePersistedNetworkFilter } from '#hooks/usePersistedNetworkFilter.tsx';
import { configOverridesDefaultValues } from '#schemas/config-override/config-overrides-default-values.ts';
import { tagNameSchema } from '#schemas/tag/name.ts';
import { useFocusTabWithErrors } from '#screens/hosts/form/hooks/useFocusTabWithErrors.ts';
import { useResetOverridesFromTags } from '#screens/hosts/form/hooks/useResetOverridesFromTags.ts';
import { useSuspenseHostFormDeps } from '#screens/hosts/form/hooks/useSuspenseHostFormDeps.ts';
import { hostsListTo } from '#screens/hosts/listLocation.ts';
import { hostFlavorFlags, type HostKind } from '#util/host.ts';
import { getCidrV4, getCidrV6 } from '#util/networkCidrs.ts';
import { makeIsEditableConfigOverrideKey, withUnknownOverrides } from '#util/unknownConfigOverrides.ts';
import { checkIsFormValid } from '#util/validation/checkIsFormValid.ts';
import { SPR } from '#util/validation/zod-utils.ts';
import { HostAdvancedFormPart } from './advanced/HostAdvancedFormPart.tsx';
import { mapFormToOverrides } from './advanced/mappers/mapFormToOverrides.ts';
import { filterTagProvidedOverrides } from './filterTagProvidedOverrides.ts';
import { makeDefaultValues } from './general/form-data.ts';
import { HostGeneralFormPart } from './general/HostGeneralFormPart.tsx';
import { useCreatableRole } from './hooks/useCreatableRole.ts';
import { useHostConfigOverrides } from './hooks/useHostConfigOverrides.ts';
import { useHostTabs } from './hooks/useHostTabs.ts';
import { useRestartRequired } from './hooks/useRestartRequired.ts';
import { useSavedHostOverrides } from './hooks/useSavedHostOverrides.ts';
import { useTagOverrideMap } from './hooks/useTagOverrideMap.ts';
import { makeHostFormSchema, type HostFormData, type HostFormTransformedData } from './host-form-schema.ts';
import { useAddHost, useEditHost, useUpdateHostCache } from './HostForm.queries.ts';
import { isHostUnreachableWithoutRole } from './isHostUnreachableWithoutRole.ts';
import { makeMapHostErrorsToForm, mapListErrorsToForm } from './mapErrorsToForm.ts';
import { RestartHostModal } from './restart-modal/RestartHostModal.tsx';
import styles from './HostForm.module.css';

type EditProps = {
  onSuccess: () => void;
  /** Must be provided when editing an existing host */
  hostId: string;
  /** Which flavor of host is being edited; decides fields, copy, and where cancel/save land */
  kind: HostKind;
  defaultValues: HostFormData;
  /** Provided when editing */
  staticAddresses: string[] | undefined;
  /** Provided when editing */
  configOverrides: ConfigOverride[];
};

type NewProps = {
  /** Once we successfully post the new host, let the parent know its id so it can show the enroll page */
  onSuccess: (hostId: string) => void;
  /** Only provided when editing an existing host */
  hostId: null;
  /** Which flavor of host is being created; decides fields, copy, and where cancel/save land */
  kind: HostKind;
};

type Props = NewProps | EditProps;

export function HostForm(props: Props) {
  const { hostId, kind, onSuccess } = props;
  const { defaultNetworkID, networks, v4CIDRs, v6CIDRs, allTags, shouldShowNetworkSelect } = useSuspenseHostFormDeps();
  const persistedNetworkFilter = usePersistedNetworkFilter();

  let defaultValues = {
    ...makeDefaultValues(kind),
    configOverrides: configOverridesDefaultValues,
    networkID: defaultNetworkID,
  } satisfies HostFormData;
  if (hostId) {
    defaultValues = props.defaultValues;
  }

  const { tabLinkProps } = useHostTabs();
  const { data: { DNS_CONFIG_OVERRIDES } = {} } = useSuspenseFeatureFlagsQuery();

  const {
    hostOverrides,
    clearHostOverride,
    savedHostOverrides,
    unknownOverrides,
    savedUnknownOverrides,
    removedUnknownIndices,
    removeUnknownOverride,
    restoreUnknownOverride,
    hasRemovedUnknownOverrides,
  } = useHostConfigOverrides(
    props.hostId ? props.configOverrides : undefined,
    makeIsEditableConfigOverrideKey(Boolean(DNS_CONFIG_OVERRIDES))
  );

  const isEditing = Boolean(hostId?.length);

  const formContext = useForm({
    defaultValues,
    resolver: zodResolver(makeHostFormSchema(kind, v4CIDRs, v6CIDRs)),
  });

  const { handleSubmit, setError, clearErrors, formState, getValues } = formContext;
  const { isSubmitting, errors, isSubmitted, submitCount, defaultValues: currentDefaults } = formState;
  const isValid = checkIsFormValid(errors);

  const addHost = useAddHost();
  const editHost = useEditHost();
  const updateHostCache = useUpdateHostCache();

  let staticAddresses: string[] | undefined;
  if (props.hostId) {
    staticAddresses = props.staticAddresses;
  }
  const extraAddressCount = kind === 'lighthouse' && staticAddresses ? Math.max(staticAddresses.length - 1, 0) : 0;

  useResetOverridesFromTags(formContext, hostOverrides);
  const { hasDirtyOverrides, savedOverrides, isSavedOverridesReady } = useSavedHostOverrides(
    formContext,
    // @ts-expect-error technically currentDefaults is deep partial, even though we make sure to initialize all defaults.  Seems fine?
    currentDefaults,
    hostOverrides
  );
  const { createOrUseExistingRole, hasCreatedRole } = useCreatableRole(formContext);
  const focusTabWithErrors = useFocusTabWithErrors(kind);
  const { restartHost, setRestartHost, checkRestartRequired } = useRestartRequired();

  // getValues() reads a snapshot rather than subscribing, so the override comparisons below only
  // re-run when this component re-renders: subscribe to the override subtree to make them react.
  useWatch({ control: formContext.control, name: 'configOverrides' });
  const currentValues = getValues();
  const validTags = currentValues.tags.map((t) => SPR(tagNameSchema.safeParse(t)).data).filter(Boolean);
  const overrideMap = useTagOverrideMap(validTags);
  const [selectedRoleID, selectedTags] = useWatch({ control: formContext.control, name: ['roleID', 'tags'] });
  const isUnreachableWithoutRole = isHostUnreachableWithoutRole(selectedRoleID, selectedTags, allTags);

  // Calculate our own dirty state because saved overrides aren't used as the default values,
  // and because we need to dirty when a host override is cleared
  const isDirty =
    hasDirtyOverrides ||
    hasRemovedUnknownOverrides ||
    !deepEqual({ ...currentValues, configOverrides: undefined }, { ...defaultValues, configOverrides: undefined }) ||
    Boolean(props.hostId && !deepEqual(hostOverrides, savedHostOverrides));

  // When the form is submitted, if there are errors, we might need to focus a different tab.
  const previousSubmitCountRef = useRef<number | null>(null);
  useEffect(() => {
    if (submitCount !== previousSubmitCountRef.current) {
      previousSubmitCountRef.current = submitCount;
      if (!isValid) {
        focusTabWithErrors(errors);
      }
    }
  }, [errors, focusTabWithErrors, isValid, submitCount]);

  // Scroll to bottom if we get a tag error
  useEffect(() => {
    if ((errors.tags?.message?.length ?? 0) > 0) {
      window.scrollTo(0, document.body.scrollHeight);
    }
  }, [errors.tags?.message?.length]);

  // Wait until we have loaded host overrides when editing
  if (!isSavedOverridesReady) return null;

  /**
   * Submission does two things:
   *
   * 1) Creates a new role if necessary
   * 2) Creates a host, using either the new role or a provided roleId
   *
   * Note, there's a possible intermediate state, where the role creation succeeded,
   * but host creation failed (duplicate name, for example).  In this case, we'll use
   * the ID from the previously-created new role.
   */
  const onSubmit = handleSubmit(async (data: HostFormTransformedData) => {
    clearErrors('root.global');

    const roleID = await createOrUseExistingRole(data);

    // Only try to create a host if we didn't blow up when creating the role
    if (typeof roleID === 'undefined') {
      return;
    }

    // Only lighthouses advertise where they can be dialed, and they compose it from two fields.
    // Hosts and relays keep whatever the server already had. The form only exposes a lighthouse's
    // first advertised address; any further API-created addresses pass through unchanged so an
    // unrelated edit can't truncate them.
    const publicAddress =
      kind === 'lighthouse' ?
        [`${data.publicIPOrHostname}:${data.listenPort}`, ...(staticAddresses?.slice(1) ?? [])]
      : undefined;

    // Track the index each IP lands at in the sent array so the error handler
    // can route `ipAddresses[N]` errors back to the correct field. Edit skips
    // ipAddresses entirely, so -1 signals "not sent."
    let v4Index = -1;
    let v6Index = -1;

    try {
      // Editing, but not using the isEditing variable because of TS narrowing
      if (hostId) {
        const editValues = {
          id: hostId,
          values: {
            name: data.name,
            roleID,
            listenPort: data.listenPort,
            staticAddresses: publicAddress ?? staticAddresses,
            tags: data.tags,
            configOverrides: withUnknownOverrides(
              filterTagProvidedOverrides(mapFormToOverrides(data.configOverrides), overrideMap, hostOverrides),
              unknownOverrides
            ),
          },
        };
        const updatedHost = await editHost.mutateAsync(editValues);
        await updateHostCache(updatedHost);
        // Has the user changed a setting that requires a restart?  If so, let the user know
        if (
          checkRestartRequired(
            data,
            {
              configOverrides: assert(savedOverrides),
              tags: defaultValues.tags,
              listenPort: defaultValues.listenPort,
            },
            allTags,
            updatedHost.metadata
          )
        ) {
          // call onSuccess in the modal
          setRestartHost({ id: updatedHost.id, platform: updatedHost.metadata.platform });
        } else {
          onSuccess();
        }
      } else {
        const selectedNetwork = networks.find((n) => n.id === data.networkID);
        const networkCidrV4 = selectedNetwork ? getCidrV4(selectedNetwork) : undefined;
        const networkCidrV6 = selectedNetwork ? getCidrV6(selectedNetwork) : undefined;
        const isDualStack = Boolean(networkCidrV4 && networkCidrV6);

        const ipAddresses: string[] = [];
        if (data.ipv4Enabled) {
          if (data.ipv4Address.length > 0) {
            v4Index = ipAddresses.length;
            ipAddresses.push(data.ipv4Address);
          } else if (isDualStack && networkCidrV4) {
            // dual-stack: blank = auto-assign; send CIDR as the API signal
            v4Index = ipAddresses.length;
            ipAddresses.push(networkCidrV4);
          }
        }
        if (data.ipv6Address.length > 0) {
          v6Index = ipAddresses.length;
          ipAddresses.push(data.ipv6Address);
        }

        const newHost = await addHost.mutateAsync({
          name: data.name,
          networkID: data.networkID,
          roleID,
          ...hostFlavorFlags(kind),
          staticAddresses: publicAddress,
          ipAddresses,
          listenPort: data.listenPort,
          tags: data.tags,
          configOverrides: filterTagProvidedOverrides(mapFormToOverrides(data.configOverrides), overrideMap, []),
        });
        await updateHostCache(newHost);
        onSuccess(newHost.id);
      }
    } catch (error) {
      if (error instanceof ValidationError) {
        const mappedError = mapListErrorsToForm(mapFormToOverrides(data.configOverrides), error);
        handleServerFormError(mappedError, makeMapHostErrorsToForm(setError, kind, { v4Index, v6Index }));
      } else {
        trackError(error);
        setError('root.global', { message: `We couldn’t ${isEditing ? 'edit' : 'add'} the ${kind}, please try again` });
      }
    }
  });

  return (
    <FormProvider {...formContext}>
      {extraAddressCount > 0 ?
        <Banner className="mb-16" Icon={InfoCircle}>
          This lighthouse advertises {extraAddressCount + 1} static addresses. Only the first can be edited here; the
          rest are kept unchanged when you save.
        </Banner>
      : null}
      <form method="post" id="host-form" onSubmit={onSubmit}>
        {errors.root?.global?.message && <ErrorMessage className="mb-24">{errors.root.global.message}</ErrorMessage>}
        <div className={styles.HostForm}>
          <TabLinks
            tabs={
              <>
                <TabLink {...tabLinkProps.general}>General</TabLink>
                <TabLink {...tabLinkProps.advanced}>Advanced</TabLink>
              </>
            }
          >
            <HostGeneralFormPart
              hasCreatedRole={hasCreatedRole}
              isEditing={isEditing}
              kind={kind}
              networks={networks}
              shouldShowNetworkSelect={shouldShowNetworkSelect}
            />
            <HostAdvancedFormPart
              hostType={kind}
              overrideMap={overrideMap}
              hostOverrides={hostOverrides}
              clearHostOverride={clearHostOverride}
              unknownOverrides={savedUnknownOverrides}
              removedUnknownIndices={removedUnknownIndices}
              removeUnknownOverride={removeUnknownOverride}
              restoreUnknownOverride={restoreUnknownOverride}
            />
          </TabLinks>
        </div>
        <ConfirmChangesBanner
          isOpen={isDirty}
          title={
            isSubmitted && !isValid ? 'Unable to save.  Please update and try again.'
            : !isUnreachableWithoutRole ?
              undefined
            : kind === 'lighthouse' ?
              'You will not be able to ping this lighthouse.'
            : kind === 'relay' ?
              'This host will function as a relay, but will not be reachable directly.'
            : 'This host will not be reachable by other hosts.'
          }
          primaryButton={
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              Save
            </Button>
          }
          secondaryButton={
            <CancelLinkButton fallbackTo={isEditing ? `/hosts/${hostId}` : hostsListTo(kind, persistedNetworkFilter)} />
          }
        />
        <RestartHostModal
          isOpen={Boolean(restartHost)}
          platform={restartHost?.platform ?? null}
          onClose={() => {
            if (restartHost) {
              onSuccess(restartHost.id);
            }
            setRestartHost(null);
          }}
        />
      </form>
    </FormProvider>
  );
}
